import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Product } from '../Module/Product.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

// eslint-disable-next-line @typescript-eslint/no-require-imports
const JsBarcode = require('jsbarcode') as (element: object, data: string, options?: object) => void;

export interface LabelItem {
  product: Product;
  barcodeSvg: SafeHtml;
}

export interface LabelSize {
  id: string;
  label: string;
  widthMm: number;
  heightMm: number;
  /** Preview card dimensions (px) */
  previewW: number;
  previewH: number;
  /** Barcode SVG height for print (mm) */
  barcodeHeightMm: number;
  /** Barcode bar height (px) used by JsBarcode */
  barcodeBarHeight: number;
  /** fontSize for JsBarcode */
  barcodeFontSize: number;
  /** Barcode bar width factor for JsBarcode */
  barcodeBarWidth: number;
  /** Margin inside JsBarcode SVG */
  jsbarcodeMargin: number;
}

const LABEL_SIZES: LabelSize[] = [
  {
    id: '50x25',
    label: '50 × 25 mm',
    widthMm: 50,
    heightMm: 25,
    previewW: 160,
    previewH: 80,
    barcodeHeightMm: 17,
    barcodeBarHeight: 40,
    barcodeFontSize: 11,
    barcodeBarWidth: 1.4,
    jsbarcodeMargin: 2
  },
  {
    id: '30x15',
    label: '30 × 15 mm',
    widthMm: 30,
    heightMm: 15,
    previewW: 120,
    previewH: 60,
    barcodeHeightMm: 8.5,
    barcodeBarHeight: 24,
    barcodeFontSize: 7,
    barcodeBarWidth: 1.3,
    jsbarcodeMargin: 0
  },
];

const DEFAULT_SIZE_KEY = 'barcode_label_default_size';

@Component({
  selector: 'app-print-barcode-dialog',
  templateUrl: './print-barcode-dialog.component.html',
  styleUrls: ['./print-barcode-dialog.component.css']
})
export class PrintBarcodeDialogComponent implements OnInit {

  products: Product[];
  copies: number = 1;
  labelItems: LabelItem[] = [];
  isPrinting: boolean = false;

  readonly labelSizes: LabelSize[] = LABEL_SIZES;
  selectedSize: LabelSize;
  defaultSaved: boolean = false;   // shows brief ✓ feedback

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { products: Product[] },
    private dialogRef: MatDialogRef<PrintBarcodeDialogComponent>,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {
    this.products = data.products;

    // Restore saved default, fallback to 50×25
    const savedId = localStorage.getItem(DEFAULT_SIZE_KEY);
    this.selectedSize = LABEL_SIZES.find(s => s.id === savedId) ?? LABEL_SIZES[0];
  }

  ngOnInit(): void {
    this.buildLabelItems();
  }

  get totalLabels(): number {
    return this.products.length * (this.copies || 1);
  }

  onCopiesChange(): void {
    if (!this.copies || this.copies < 1) this.copies = 1;
    if (this.copies > 200) this.copies = 200;
    this.buildLabelItems();
  }

  selectSize(size: LabelSize): void {
    this.selectedSize = size;
    this.defaultSaved = false;
    this.buildLabelItems();
  }

  setAsDefault(): void {
    localStorage.setItem(DEFAULT_SIZE_KEY, this.selectedSize.id);
    this.defaultSaved = true;
    setTimeout(() => { this.defaultSaved = false; this.cdr.detectChanges(); }, 2000);
  }

  buildLabelItems(): void {
    const items: LabelItem[] = [];
    for (const product of this.products) {
      const barcodeSvg = this.generateBarcodeSvgPreview(String(product.codeBare ?? '0'));
      for (let i = 0; i < this.copies; i++) {
        items.push({ product, barcodeSvg });
      }
    }
    this.labelItems = items;
    this.cdr.detectChanges();
  }

  // ── Preview barcode (dialog display) ──────────────────────────────────

  private generateBarcodeSvgPreview(code: string): SafeHtml {
    try {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      JsBarcode(svg as unknown as object, code, {
        format: 'CODE128',
        width: this.selectedSize.barcodeBarWidth,
        height: this.selectedSize.barcodeBarHeight,
        fontSize: this.selectedSize.barcodeFontSize,
        margin: this.selectedSize.jsbarcodeMargin,
        textMargin: 1,
        displayValue: true,
        background: '#ffffff',
        lineColor: '#000000'
      });
      // Set viewBox for responsive scaling inside the preview card
      const w = parseFloat(svg.getAttribute('width') || '200');
      const h = parseFloat(svg.getAttribute('height') || '60');
      svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      return this.sanitizer.bypassSecurityTrustHtml(svg.outerHTML);
    } catch {
      return this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="40">
           <rect width="120" height="40" fill="#f9f9f9" stroke="#ccc"/>
           <text x="60" y="24" font-size="9" text-anchor="middle" fill="#999">Code invalide</text>
         </svg>`
      );
    }
  }

  // ── Main print method ──────────────────────────────────────────────────

  async printLabels(): Promise<void> {
    this.isPrinting = true;
    this.cdr.detectChanges();
    try {
      // Try print-server (port 3001) — raw TSPL2 via Windows Spooler, no dialog
      const items = this.products.map(p => ({
        code: String(p.codeBare ?? '0'),
        name: (p.price != null ? p.price + ' DH - ' : '') + (p.name ?? '')
      }));
      let printed = false;
      try {
        // The print server always runs on the CLIENT machine (not the remote host),
        // so we must always target localhost regardless of where the app is hosted.
        const resp = await fetch('http://localhost:3001/print-label', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            products: items,
            copies: this.copies,
            printerName: 'Xprinter XP-460B',
            labelWidth: this.selectedSize.widthMm,
            labelHeight: this.selectedSize.heightMm
          })
        });
        if (resp.ok) { const r = await resp.json(); printed = r.success === true; }
      } catch { /* server not running → fallback */ }

      let success = true;
      if (!printed) {
        success = this.fallbackWindowPrint();
      }

      if (success) {
        this.dialogRef.close(true);
      }
    } finally {
      this.isPrinting = false;
      this.cdr.detectChanges();
    }
  }

  // ── Fallback: browser print dialog ──────────────────────────────────

  private fallbackWindowPrint(): boolean {
    const pw = window.open('', '_blank', 'width=600,height=400');
    if (!pw) { alert('Veuillez autoriser les fenêtres popup pour imprimer.'); return false; }

    const { id, widthMm, heightMm, barcodeHeightMm } = this.selectedSize;
    const isSmall = id === '30x15';

    const nameFontPt = isSmall ? 5.5 : 7.5;
    const paddingStr = isSmall ? '0.3mm 0.7mm 0.3mm 0.7mm' : '0.8mm 1.5mm 0.8mm 1.5mm';
    const printableWidthMm = isSmall ? widthMm - 1.4 : 47;

    const labelsHtml = this.products.flatMap(product => {
      const svg  = this.buildSvgForPrint(String(product.codeBare ?? '0'));
      const priceStr = product.price != null ? product.price + ' DH' : '';
      const fullName = (priceStr ? priceStr + ' - ' : '') + (product.name ?? '');
      const name = fullName.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const priceOnly = priceStr.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const codeNum = String(product.codeBare ?? '0').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      if (isSmall) {
        // 30×15mm: barcode full width, price left + code right below, NO product name
        const priceSpan = priceOnly ? `<span class="lprice">${priceOnly}</span>` : '';
        return Array.from({ length: this.copies }, () =>
          `<div class="label">${svg}<div class="lcode-row">${priceSpan}<span class="lbarnum">${codeNum}</span></div></div>`);
      } else {
        // 50×25mm: name + barcode (unchanged)
        return Array.from({ length: this.copies }, () =>
          `<div class="label"><div class="lname-wrap"><span class="lname">${name}</span></div>${svg}</div>`);
      }
    }).join('');

    const smallStyles = isSmall ? `
.label { justify-content: flex-start; }
.label svg { width:${printableWidthMm}mm !important; height:${barcodeHeightMm}mm !important; display:block; margin:0 auto; }
.lcode-row {
  width:100%; display:flex; justify-content:center; align-items:center; gap: 4px;
  margin-top:0.4mm; padding:0;
  font-family:"Arial", sans-serif; color:#000;
  white-space:nowrap; overflow:hidden;
}
.lprice  { font-size:6pt; font-weight:900; letter-spacing: -0.1px; }
.lbarnum { font-size:6pt; font-weight:bold; letter-spacing: 0.2px; }` : `
.lname-wrap { width:100%; text-align:center; margin-bottom:0.2mm; line-height:1; overflow:hidden; }
.lname {
  display:block; width:100%;
  font:bold ${nameFontPt}pt Arial, sans-serif;
  text-align:center; white-space:nowrap;
  overflow:hidden; text-overflow:ellipsis;
  color:#000;
}
.label svg { width:${printableWidthMm}mm !important; height:${barcodeHeightMm}mm !important; display:block; margin:0 auto; }`;

    pw.document.write(`<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
<title>Impression codes-barres</title><style>
* { margin:0; padding:0; box-sizing:border-box; }
@page { size:${widthMm}mm ${heightMm}mm; margin:0; }
html,body { width:${widthMm}mm; height:${heightMm}mm; background:#fff; overflow:hidden; }
.label {
  width:${widthMm}mm; height:${heightMm}mm;
  display:flex; flex-direction:column;
  align-items:center;
  padding:${paddingStr};
  overflow:hidden; page-break-after:always; break-after:page;
}
${smallStyles}
</style></head><body>${labelsHtml}</body></html>`);
    pw.document.close();
    setTimeout(() => { pw.focus(); pw.print(); pw.close(); }, 300);
    return true;
  }

  private buildSvgForPrint(code: string): string {
    try {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      const isSmall = this.selectedSize.id === '30x15';
      JsBarcode(svg as unknown as object, code, {
        format: 'CODE128',
        width: this.selectedSize.barcodeBarWidth,
        height: this.selectedSize.barcodeBarHeight,
        fontSize: this.selectedSize.barcodeFontSize,
        margin: this.selectedSize.jsbarcodeMargin,
        textMargin: 1,
        displayValue: true,
        background: '#ffffff',
        lineColor: '#000000'
      });
      const w = parseFloat(svg.getAttribute('width') || '200');
      const h = parseFloat(svg.getAttribute('height') || '60');
      svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      // 30×15: barcode is centered, printable width 28mm
      const printableWidthMm = isSmall ? 28 : 47;
      svg.setAttribute('width', `${printableWidthMm}mm`);
      svg.setAttribute('height', `${this.selectedSize.barcodeHeightMm}mm`);
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      return svg.outerHTML;
    } catch { return ''; }
  }
}
